import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/sgokit.svg';

/**
 * The console chrome shared by the admin console (`AdminLayout`) and a
 * school's own console (`PartnerLayout`): a navy SGOKit sidebar (brand mark,
 * the signed-in organization, a nav the caller supplies), the signed-in
 * identity and a logout control, framing a warm-paper work area. Styled in the
 * SGOKit platform identity (sgokit.css, `.sgk-admin`).
 *
 * `testIdPrefix` namespaces the shell's own `data-testid`s (`admin-org-name`,
 * `partner-logout-button`, …) so each console keeps its own stable hooks.
 * See `AdminLayout` for what `title`/`subtitle`/`actions`/`fullBleed`/`wide`
 * do.
 */
const ConsoleShell: React.FC<{
  nav: React.ReactNode;
  testIdPrefix: string;
  orgLabel?: string;
  children: React.ReactNode;
  title?: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  fullBleed?: boolean;
  wide?: boolean;
}> = ({ nav, testIdPrefix, orgLabel = 'Organization', children, title, subtitle, actions, fullBleed, wide }) => {
  const { user, tenantContext, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="sgk-site">
      <div className={`sgk-admin${fullBleed ? ' sgk-admin--bleed' : ''}`}>
        <aside className="sgk-admin__sidebar">
          <div className="sgk-admin__brand">
            <img className="sgk-brand__logo" src={logo} alt="" />
            <span className="sgk-brand__name">SGO<b>Kit</b></span>
          </div>

          <div className="sgk-admin__org">
            <div className="sgk-admin__org-label">{orgLabel}</div>
            <div className="sgk-admin__org-name" data-testid={`${testIdPrefix}-org-name`}>
              {tenantContext?.name ?? user?.organization_name ?? '—'}
            </div>
          </div>

          <nav className="sgk-admin__nav">{nav}</nav>

          <div className="sgk-admin__foot">
            <div className="sgk-admin__user" data-testid={`${testIdPrefix}-user-email`}>
              {user?.email}
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="sgk-admin__signout"
              data-testid={`${testIdPrefix}-logout-button`}
            >
              Sign out
            </button>
          </div>
        </aside>

        <main className="sgk-admin__main">
          {title && (
            <div className="sgk-admin__hero">
              <div className="sgk-admin__herorow">
                <div>
                  <h1 className="sgk-admin__herotitle">{title}</h1>
                  {subtitle && <p className="sgk-admin__herosub">{subtitle}</p>}
                </div>
                {actions && <div className="sgk-admin__heroactions">{actions}</div>}
              </div>
            </div>
          )}
          {/* fullBleed skips the 780px-max centered column — a page like the
              form builder (palette · canvas · settings-rail) needs the whole
              width next to the sidebar, unlike every other settings page's
              narrow form. `wide` keeps the centered column (and its edge
              padding) but widens the cap for a data-dense page like the
              Partners table, so the breathing room lands inside the table's
              own columns rather than in empty margins on either side. */}
          {fullBleed ? (
            <div className="sgk-admin__bleed">{children}</div>
          ) : (
            <div className={`sgk-admin__inner${wide ? ' sgk-admin__inner--wide' : ''}`}>
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ConsoleShell;
